import { Position } from "geojson";
import mapboxgl, { PointLike } from "mapbox-gl";
import { PropsWithChildren, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { GaiaMapContext } from "../../context/GaiaMapContext";

type GaiaMapMarkerProps = PropsWithChildren<{
    coordinates: Position;
    onMouseLeave?: () => void;
    offset?: PointLike;
}>;

export function GaiaMapMarker({ coordinates, children, onMouseLeave, offset = [0, 0] }: GaiaMapMarkerProps) {
    const { mapRef, isMapLoaded } = useContext(GaiaMapContext);

    const markerRef = useRef<mapboxgl.Marker | null>(null);
    const [markerElement, setMarkerElement] = useState<HTMLElement | null>(null);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <coordinates and children are updated separately to avoid problems unmounting>
    useEffect(() => {
        if (mapRef === undefined) return;
        if (!isMapLoaded || !mapRef.current) return;
        if (markerRef.current) return;

        const el = document.createElement("div");
        setMarkerElement(el);

        markerRef.current = new mapboxgl.Marker({
            element: el,
            anchor: "top-left",
            offset,
        })
            .setLngLat([coordinates[0], coordinates[1]])
            .addTo(mapRef.current);

        return () => {
            markerRef.current?.remove();
            markerRef.current = null;
            setMarkerElement(null);
        };
    }, [isMapLoaded, mapRef]);

    useEffect(() => {
        if (markerRef.current) {
            markerRef.current.setLngLat([coordinates[0], coordinates[1]]);
        }
    }, [coordinates]);

    return (
        markerElement &&
        createPortal(
            // biome-ignore lint/a11y/noStaticElementInteractions: Map marker wrapper requires mouse events for interaction
            <div onMouseLeave={onMouseLeave}>{children}</div>,
            markerElement,
        )
    );
}
